import {
  addToast,
  Button,
  OutlinedSelectOptionType,
  Select,
  TextInput,
} from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import { useLayoutState } from '../../../../hooks/useLayoutState';
import { useLoadingState } from '../../../../hooks/useLoadingState';
import { AcademicOffersTypeDuplicate as AcademicOffers } from '../../../../types/AcademicOffersType';
import {
  downloadInicioTariffTemplate,
  getAllAcademicOffersRequest,
} from '../api/tariff';
import BulkInicioTariffUploadModal from './parts/BulkInicioTariffUploadModal';
import TariffTable from './parts/TariffTable';

function Tariff() {
  const { t } = useTranslation();
  const { showTabs } = useLayoutState();
  const [academicOffers, setAcademicOffers] = useState<AcademicOffers[]>([]);
  const [referralOffer, setReferralOffer] = useState<AcademicOffers | null>(
    null,
  );
  const [
    currentAcademicOffers,
    setCurrentAcademicOffers,
  ] = useState<AcademicOffers>();
  const {
    loading,
    setLoading,
    errorLoading,
    setErrorLoading,
  } = useLoadingState();
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Si la oferta seleccionada no tiene planes de estudio, no hay nada que
  // preciar: se deshabilitan la descarga y la carga de plantilla. El conteo lo
  // reporta la tabla (TariffTable) tras cargar.
  const [hasStudyPlans, setHasStudyPlans] = useState(false);

  const handleTableLoaded = useCallback(
    (total: number) => setHasStudyPlans(total > 0),
    [],
  );

  const selectOptions = academicOffers.map<OutlinedSelectOptionType>(
    (offer) => {
      return {
        label: offer.period.name,
        value: offer.id,
      };
    },
  );

  const handleChange = (option: OutlinedSelectOptionType) => {
    const current = academicOffers.find(({ id }) => option.value === id);
    setCurrentAcademicOffers(current);
    setReferralOffer(current?.duplicatedAcademicOffer ?? null);
    setHasStudyPlans(false);
  };

  const onDownloadTemplate = useCallback(async () => {
    if (!currentAcademicOffers) {
      return;
    }
    setDownloadingTemplate(true);
    const res = await downloadInicioTariffTemplate(currentAcademicOffers.id, {
      inscriptionLabel: t('tariffAndTuition.tariff.headerInscription'),
      tuitionLabel: t('tariffAndTuition.tariff.headerTuition'),
      creditLabel: t('tariffAndTuition.tariff.headerCredit'),
      equivalenceLabel: t('tariffAndTuition.tariff.headerEquivalence'),
    });
    if (res.error || !res.data) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('common.errors.download'),
      });
    } else {
      const a = document.createElement('a');
      document.body.appendChild(a);
      const url = window.URL.createObjectURL(res.data as Blob);
      a.href = url;
      a.download = 'plantilla-aranceles-inicio.xlsx';
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        a.remove();
      }, 0);
    }
    setDownloadingTemplate(false);
  }, [currentAcademicOffers, t]);

  const getAllAcademicOffer = useCallback(async () => {
    setLoading(true);
    const { data, error } = await getAllAcademicOffersRequest();
    if (error) {
      setErrorLoading(error.code);
    } else if (data) {
      setErrorLoading(undefined);
      setAcademicOffers(data.data);
    }
    setLoading(false);
  }, [setErrorLoading, setLoading]);

  useEffect(() => {
    getAllAcademicOffer();
  }, [getAllAcademicOffer]);

  useEffect(() => {
    showTabs();
  }, [showTabs]);

  if (errorLoading) {
    return (
      <DisplayError
        insideCard
        textBody={errorLoading}
        retryAction={() => getAllAcademicOffer()}
        loadingAction={loading}
      />
    );
  }

  if (loading) {
    return <Loading insideCard />;
  }

  return (
    <div className="g-table-container pt-4">
      <p className="fs-20 text-uppercase text-primary fw-700 ">
        {t('tariffAndTuition.tariff.title')}
      </p>
      <p> {t('tariffAndTuition.tariff.subTitle')}</p>
      <Row>
        <Col md={6}>
          <Select
            name="selection-period"
            label={t('tariffAndTuition.tariff.selectLabel')}
            isClearable={false}
            options={selectOptions}
            onChange={handleChange}
          />
        </Col>
        {currentAcademicOffers && (
          <Col md={6}>
            <TextInput
              name="selection-period"
              label={t('tariffAndTuition.tariff.inputLabel')}
              value={referralOffer?.period.name ?? 'Sin referencia'}
              disabled
            />
          </Col>
        )}
      </Row>
      <br className="mt-4" />
      {currentAcademicOffers && (
        <>
          <Row className="mb-3">
            <Col md={6}>
              <p className="fs-18 text-uppercase text-primary fw-700">
                {t('tariffAndTuition.tariff.individualConfigTitle')}
              </p>
              <p>{t('tariffAndTuition.tariff.individualConfigDescription')}</p>
            </Col>
            <Col md={6}>
              <p className="fs-18 text-uppercase text-primary fw-700">
                {t('tariffAndTuition.tariff.massiveConfigTitle')}
              </p>
              <p>{t('tariffAndTuition.tariff.massiveConfigDescription')}</p>
              <Button
                fullwidth
                type="button"
                size="sm"
                outlined
                icon="download"
                text={t('tariffAndTuition.tariff.downloadTemplate')}
                loading={downloadingTemplate}
                disabled={!hasStudyPlans}
                onClick={onDownloadTemplate}
              />
              <Button
                fullwidth
                type="button"
                size="sm"
                icon="upload"
                text={t('tariffAndTuition.tariff.uploadTemplate')}
                disabled={!hasStudyPlans}
                onClick={() => setUploadModalOpen(true)}
              />
              {!hasStudyPlans && (
                <p className="fs-14 text-gray mt-2 mb-0">
                  {t('tariffAndTuition.tariff.noStudyPlans')}
                </p>
              )}
            </Col>
          </Row>
          <TariffTable
            key={reloadKey}
            academicOfferId={currentAcademicOffers.id}
            onLoaded={handleTableLoaded}
          />
          <BulkInicioTariffUploadModal
            isOpen={uploadModalOpen}
            academicOfferId={currentAcademicOffers.id}
            onClose={() => setUploadModalOpen(false)}
            onUploaded={() => setReloadKey((k) => k + 1)}
          />
        </>
      )}
    </div>
  );
}
export default Tariff;
